import type { GameDocument } from "@/lib/cms/prismicio-types";
// biome-ignore lint/style/useImportType: <explanation>
import { createClient } from "@/prismic";
import { GameTags, sanitizeTag } from "@cms/utils";
import { type Content, filter as prismicFilter } from "@prismicio/client";
import { notifyError } from "../monitoring";

export const gameGraph = `
  game {
    ...gameFields
    socials {
      ...socialsFields
      network {
        ... on social_network {
          ...social_networkFields
        }
      }
    }
    quests {
      ...questsFields
      quest {
        ...on game_quest {
          ...game_questFields
        }
      }
    }
    publisher {
      ...on games_company {
        ...games_companyFields
      }
    }
    studio {
      ...on games_company {
        ...games_companyFields
      }
    }
    age_raiting {
      ...on game_age_raiting {
        ...game_age_raitingFields
      }
    }
    genres {
      ...genresFields
      genre {
        ...on game_genre {
          ...game_genreFields
        }
      }
    }
    platforms {
      ...platformsFields
      platform {
        ...on game_platform {
          ...game_platformFields
        }
      }
    }
    devices {
      ...devicesFields
      device {
        ...on game_device {
          ...game_deviceFields
        }
      }
    }
  }
`;

const graphQuery = `{
  ${gameGraph}
}`;

export const getGames = async (
  client: ReturnType<typeof createClient>,
  tags: string[] = [],
): Promise<GameDocument[]> => {
  try {
    const games = await client.getAllByType("game", {
      graphQuery,
      filters: tags?.length ? [prismicFilter.at("document.tags", tags)] : [],
    });
    return games as GameDocument[];
  } catch (error) {
    notifyError(error, "getGames");
    return [];
  }
};

export const getGameByUid = async (
  client: ReturnType<typeof createClient>,
  uid: string,
): Promise<GameDocument | null> => {
  try {
    const game = await client.getByUID("game", uid, { graphQuery });
    return game as GameDocument;
  } catch (error) {
    notifyError(error, "getGame", { uid });
    return null;
  }
};

export type GameCollection = Record<string, Content.GameDocument[]>;
export const getGameCollectionByTags = async (
  client: ReturnType<typeof createClient>,
  tags: string[],
) => {
  try {
    const gamesByTagPromise = Promise.all(
      tags.map(async (tag) => ({
        tag,
        results: await client.getAllByType("game", {
          graphQuery,
          filters: [prismicFilter.at("document.tags", [tag])],
        }),
      })),
    );

    const untaggedGamesPromise = client.getAllByType("game", {
      graphQuery,
      filters: tags.map((tag) => prismicFilter.not("document.tags", [tag])),
    });

    const [games, untaggedGames] = await Promise.all([
      gamesByTagPromise,
      untaggedGamesPromise,
    ]);

    return games.reduce(
      (acc, game) => {
        const tag = sanitizeTag(game.tag);
        acc[tag] = game.results as GameDocument[];
        return acc;
      },
      {
        [GameTags.Untagged]: untaggedGames as GameDocument[],
      } as Record<string, GameDocument[]>,
    );
  } catch (error) {
    notifyError(error, "getGameCollectionByTags", { tags });
    return {} as GameCollection;
  }
};
